import { notification } from "antd";
import cuid from "cuid";
import { get, merge } from "lodash";
import omitDeep from "omit-deep-lodash";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import graphql from "utils/api/graphql";
import { TRACER } from "utils/api/graphql/queries/tracers";
import { persistProcessRuntime } from "screens/Private/Broker/Projects/Detail/resolveProcessRuntime";
import {
  KEYS,
  PROJECT_TRACKING_FIELD,
  PROJECT_TYPES,
  SALE_MODES,
  SCREENS,
  SKILL_TYPES,
} from "utils/constants";
import {
  isAuthConnectionError,
  resolveApiErrorMessage,
} from "utils/helpers/graphql-errors";
import { readAdAttribution } from "utils/helpers/adAttribution";
import { availabilityFromError } from "../Unavailable/sourceAvailability";

const Create = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(NAME_SPACES.ERRORS);
  const [addProject] = useMutation(graphql.mutations.CREATE_PROJECT, {
    onError: (error) => {
      const availability = availabilityFromError(error);
      if (availability) {
        return navigate(
          `${SCREENS.PUBLIC.PROCESSES.UNAVAILABLE.path}/${availability}`,
          { replace: true },
        );
      }
      if (isAuthConnectionError(error)) return;
      notification.error({
        message: resolveApiErrorMessage(
          error,
          t("API", { returnObjects: true }),
        ),
      });
    },
  });
  const { src } = useParams();
  const { user, initialValues } = useLocation().state;
  const hash = window.location.hash;
  const queryString = hash.includes("?") ? "?" + hash.split("?")[1] : "";
  useQuery(TRACER, {
    fetchPolicy: "no-cache",
    variables: {
      where: { source: { id: src } },
    },
    onCompleted: ({ tracer }) => {
      const adAttribution = readAdAttribution();
      const status = omitDeep(
        get(tracer, "flow.process.processStatuses", []),
        "__typename",
      ).reduce((previous, current) =>
        Number(previous?.order) < Number(current.order) ? previous : current,
      );
      addProject({
        variables: {
          data: merge(
            {
              id: cuid(),
              statuses: [{ id: cuid(), status: { id: get(status, "id") } }],
              projectType: PROJECT_TYPES.HOT_LEAD,
              contract: {
                id: cuid(),
                saleMode: SALE_MODES.DISTANT,
              },
              contact: {
                id: cuid(),
                user: {
                  id: get(user, "id"),
                },
              },
              tracer: { id: get(tracer, "id") },
              fields: {
                processTemplate: get(tracer, "flow.process.theme"),
                skills: {
                  [SKILL_TYPES.PROCESS]: get(tracer, "flow.process.id", ""),
                },
                ...(adAttribution && {
                  [PROJECT_TRACKING_FIELD]: adAttribution,
                }),
              },
              visible: false,
            },
            initialValues,
          ),
        },
        onCompleted: ({ addProject }) => {
          const cleanTracer = omitDeep(tracer, "__typename");
          const process = get(cleanTracer, "flow.process");
          localStorage.setItem(KEYS.PROJECT_ID, addProject?.id);
          persistProcessRuntime(addProject?.id, process, cleanTracer);
          navigate(
            `${SCREENS.PRIVATE.GUEST.PROJECTS.DETAIL.path}/${addProject?.id}${queryString}`,
            {
              state: {
                process,
                tracer: cleanTracer,
              },
            },
          );
        },
      });
    },
  });
};

export default Create;
